import React, {
  FC,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import styled from 'styled-components';
import { NON_PROJECT_ACTIVITY_ID } from '../../utils';
import { CellEditorParams, CellRendererParams } from '../types';
import { Checkbox } from './component';
import { MarkedTooltip, SelectionTooltip } from './tooltips';

const BillableCheckbox = styled(Checkbox)`
  .ant-checkbox-disabled .ant-checkbox-inner {
    background-color: inherit;
  }

  .ant-checkbox-checked.ant-checkbox .ant-checkbox-inner {
    background-color: var(--black-100);
    border-color: var(--black-100);
    box-shadow: var(--shadow-highlight)
      rgba(0, 0, 0, var(--shadow-highlight-color-opacity));
  }
  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-checked::after {
    border-color: var(--black-100);
  }
`;

export const BillableRenderer: FC<CellRendererParams> = ({ value, data }) => {
  const isDisabled =
    data.projects?.value === NON_PROJECT_ACTIVITY_ID || data.marked;

  return (
    <MarkedTooltip isMarked={data.marked}>
      <BillableCheckbox checked={value} disabled={isDisabled} />
    </MarkedTooltip>
  );
};

const isEditable = ({ projects, marked }: CellRendererParams['data']) =>
  projects && !marked;

export const Selection: FC<CellRendererParams> = ({ value, data }) => {
  return (
    <SelectionTooltip data={data}>
      <Checkbox checked={data.marked ? true : value} disabled={data.marked} />
    </SelectionTooltip>
  );
};

export const BillableEditor = forwardRef<unknown, CellEditorParams>(
  ({ value: propValue, onChange, api, node, data }, ref) => {
    // ag grid instantiate editor on single click, while user expects to toggle the checkbox.
    // So we flip the original value while instantiating editor to give an feel they actually toggled the checkbox
    const isDisabled =
      data.projects?.value === NON_PROJECT_ACTIVITY_ID || data.marked;
    const [value, setValue] = useState(isDisabled ? propValue : !propValue);

    useEffect(() => {
      onChange?.({ api, node, newValue: value, data });
    }, [api, data, node, onChange, value]);

    useImperativeHandle(ref, () => {
      return {
        getValue: () => {
          return value;
        },
        isPopup: () => false,
      };
    });

    return (
      <BillableCheckbox
        checked={value}
        disabled={isDisabled}
        onChange={(e) => {
          setValue(e.target.checked);
        }}
      />
    );
  },
);

export const SelectionEditor = forwardRef<unknown, CellEditorParams>(
  (
    { value: propValue, onChange, api, node, data, rowIndex, mandateCategory },
    ref,
  ) => {
    // ag grid instantiate editor on single click, while user expects to toggle the checkbox.
    // So we flip the original value while instantiating editor to give an feel they actually toggled the checkbox
    const [value, setValue] = useState(
      isEditable(data) ? !propValue : propValue,
    );

    useEffect(() => {
      if (!data.projects) {
        api?.stopEditing();
        api?.startEditingCell({
          colKey: 'projects',
          rowIndex,
        });

        return;
      }
      if (mandateCategory && !data.category) {
        api?.stopEditing();
        api?.startEditingCell({
          colKey: 'category',
          rowIndex,
        });

        return;
      }
      onChange?.({ api, node, newValue: value, data });
    }, [api, data, mandateCategory, node, onChange, rowIndex, value]);

    useImperativeHandle(ref, () => {
      return {
        getValue: () => {
          return value;
        },
        isPopup: () => false,
      };
    });

    return (
      <Checkbox
        checked={value}
        disabled={!isEditable(data)}
        onChange={(e) => {
          setValue(e.target.checked);
        }}
      />
    );
  },
);
